"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { listingApi } from "@/lib/api/listings";
import { useI18n, getLocalizedField } from "@/i18n";
import { useLangHref } from "@/i18n/useLangHref";
import SearchableSelect from "@/components/ui/searchable-select";

// 全球区号 —— 几内亚（+224）置顶，西非邻国排前，其他主要国家紧随
// 国家名走 i18n：t('publish.countryCodes.xxx') ，见 fr.json / en.json
const AREA_CODES = [
    "+224", "+221", "+225", "+223", "+226", "+231", "+232", "+228", "+229", "+227",
    "+233", "+234", "+235", "+237", "+241", "+242", "+243",
    "+213", "+212", "+216", "+20",
    "+33", "+44", "+49", "+32", "+41", "+34", "+39", "+351",
    "+1", "+55", "+56",
    "+86", "+81", "+82", "+65", "+60", "+62", "+91", "+971", "+966",
    "+27", "+254", "+255", "+256", "+260", "+263",
];

const YEARS = Array.from({ length: new Date().getFullYear() - 1990 + 1 }, (_, i) => String(new Date().getFullYear() - i));
export default function PublishForm({ editId, onSubmitted, embedded = false }) {
    const { t, lang } = useI18n();
    const langHref = useLangHref();
    const router = useRouter();
    const STEPS = [
        { num: 1, label: t("publish.postType") },
        { num: 2, label: t("publish.brand") },
        { num: 3, label: t("publish.images") },
        { num: 4, label: t("publish.contactName") },
        { num: 5, label: t("publish.submit") },
    ];
    const [currentStep] = useState(2);
    const [type, setType] = useState("car");
    const [condition, setCondition] = useState("used");
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [brands, setBrands] = useState([]);
    const [fuelTypes, setFuelTypes] = useState([]);
    const [transmissions, setTransmissions] = useState([]);
    const [bodyTypes, setBodyTypes] = useState([]);
    const [cities, setCities] = useState([]);
    const [form, setForm] = useState({
        title: '',
        brand: '',
        model: '',
        year: '',
        mileage: '',
        fuelType: '',
        transmission: '',
        bodyType: '',
        city: '',
        color: '',
        price: '',
        description: '',
        contactName: '',
        contactPhone: '',
        whatsapp: '',
        email: '',
        negotiable: false,
    });
    const [images, setImages] = useState([]);
    const [editLoading, setEditLoading] = useState(false);
    const [existingImages, setExistingImages] = useState([]);
    // editId 从 props 来；无 editId 则为创建模式
    const isEdit = !!editId;
    const [errors, setErrors] = useState({});
    const [toast, setToast] = useState(null); // { message, type: 'success'|'error' }
    const [submitting, setSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0); // 0-100
    const [publishStep, setPublishStep] = useState(null);   // 'upload' | 'create' | null
    const [agree, setAgree] = useState(false);
    const [agreeError, setAgreeError] = useState("");
    const [areaCodePhone, setAreaCodePhone] = useState("+224");
    const [areaCodeWhatsapp, setAreaCodeWhatsapp] = useState("+224");
    const [termsOpen, setTermsOpen] = useState(false);
    const [privacyOpen, setPrivacyOpen] = useState(false);
    const fileInputRef = useRef(null);
    const dictLabel = (item) => lang === "fr" ? (item.labelFr || item.label) : (item.labelEn || item.label);
    const modalOverlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(4px)" };
    const modalContent = { background: "var(--color-surface, #fff)", borderRadius: 16, width: "100%", maxWidth: 720, maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 24px 60px rgba(0,0,0,0.3)" };
    const modalHeader = { padding: "20px 24px", borderBottom: "1px solid var(--color-border, #e5e7eb)", display: "flex", alignItems: "center", justifyContent: "space-between" };
    const modalTitle = { fontSize: 18, fontWeight: 700, margin: 0, color: "var(--color-text, #1f2937)" };
    const closeBtn = { background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "var(--color-text-secondary, #6b7280)", padding: "4px 10px", lineHeight: 1 };
    const modalBody = { padding: "24px 28px", overflowY: "auto", color: "var(--color-text-secondary, #4b5563)", fontSize: 14, lineHeight: 1.9 };
    const sectionTitle = { fontSize: 15, fontWeight: 700, color: "var(--color-text, #1f2937)", marginBottom: 8 };
    const sectionP = { margin: "0 0 10px", color: "var(--color-text-secondary, #4b5563)", lineHeight: 1.85 };

    useEffect(() => {
        const fetchCategories = async () => {
            setLoading(true);
            try {
                const raw = await listingApi.getListingCategories(1);
                const list = Array.isArray(raw) ? raw : (raw?.data || []);
                setCategories(list);
            } catch (e) {
                console.error("Failed to fetch categories", e);
            } finally {
                setLoading(false);
            }
        };
        const fetchBrands = async () => {
            try {
                const raw = await listingApi.getBrandList();
                const list = Array.isArray(raw) ? raw : (raw?.data || []);
                setBrands(list);
            } catch (e) {
                console.error("Failed to fetch brands", e);
            }
        };
        const fetchFuelTypes = async () => {
            try {
                const raw = await listingApi.getDictConfig("fuel_type");
                const list = Array.isArray(raw) ? raw : (raw?.data || []);
                // 排除 value 为空的项
                setFuelTypes(list.filter(item => item.value != null && item.value !== ""));
            } catch (e) {
                console.error("Failed to fetch fuel types", e);
            }
        };
        const fetchTransmissions = async () => {
            try {
                const raw = await listingApi.getDictConfig("transmission");
                const list = Array.isArray(raw) ? raw : (raw?.data || []);
                setTransmissions(list.filter(item => item.value != null && item.value !== ""));
            } catch (e) {
                console.error("Failed to fetch transmissions", e);
            }
        };
        const fetchBodyTypes = async () => {
            try {
                const raw = await listingApi.getListingCategories(2);
                const list = Array.isArray(raw) ? raw : (raw?.data || []);
                setBodyTypes(list);
            } catch (e) {
                console.error("Failed to fetch body types", e);
            }
        };
        const fetchCities = async () => {
            try {
                const raw = await listingApi.getCityList();
                const list = Array.isArray(raw) ? raw : (raw?.data || []);
                setCities(list);
            } catch (e) {
                console.error("Failed to fetch cities", e);
            }
        };
        fetchCategories();
        fetchBrands();
        fetchFuelTypes();
        fetchTransmissions();
        fetchBodyTypes();
        fetchCities();
    }, []);

    // ===== 编辑模式：字典加载完后，拉详情回填表单 =====
    useEffect(() => {
        if (!editId) return;
        // 等字典数据都到位才能反查 brand/city id
        if (!brands.length || !cities.length || !fuelTypes.length) return;

        let cancelled = false;
        (async () => {
            setEditLoading(true);
            try {
                const raw = await listingApi.editDetail(editId);
                const item = Array.isArray(raw) ? raw : (raw?.data || raw);
                if (!item || cancelled) return;

                // ==== 反查 brand id ====
                let brandId = '';
                if (item.brandId) {
                    brandId = String(item.brandId);
                } else if (item.brandName) {
                    const b = brands.find(x =>
                        String(x.id) === String(item.brandId) ||
                        getLocalizedField(x, "name", lang) === item.brandName ||
                        x.name === item.brandName
                    );
                    if (b) brandId = String(b.id);
                }

                // ==== 反查 city id ====
                let cityId = '';
                const cityName = item.cityNameZh || item.cityNameEn || item.cityNameFr || item.cityName;
                if (item.cityId) {
                    cityId = String(item.cityId);
                } else if (cityName) {
                    const c2 = cities.find(x =>
                        String(x.id) === String(item.cityId) ||
                        getLocalizedField(x, "name", lang) === cityName ||
                        x.name === cityName ||
                        x.nameEn === cityName ||
                        x.nameZh === cityName ||
                        x.nameFr === cityName
                    );
                    if (c2) cityId = String(c2.id);
                }

                // ==== 电话区号解析 ====
                let areaPhone = "+224", phone = "";
                let areaWa = "+224", wa = "";
                const parsePhone = (s) => {
                    if (!s) return { area: "+224", num: "" };
                    const parts = s.trim().split(/\s+/);
                    if (parts[0] && parts[0].startsWith("+")) return { area: parts[0], num: parts.slice(1).join(" ") };
                    return { area: "+224", num: s };
                };
                const pp = parsePhone(item.contactPhone);
                const ww = parsePhone(item.whatsapp);
                areaPhone = pp.area; phone = pp.num;
                areaWa = ww.area; wa = ww.num;

                // ==== 反查 fuelType / transmission ====
                const ft = (item.fuelType && fuelTypes.find(x => x.value === item.fuelType)) ? item.fuelType : '';
                const tr = (item.transmission && transmissions.find(x => x.value === item.transmission)) ? item.transmission : '';

                // ==== 反查 bodyType id（后端返回字符串如 "SUV"，option value 是 b.id）====
                let bodyId = '';
                if (item.bodyType) {
                    const byId = bodyTypes.find(b => String(b.id) === String(item.bodyType));
                    if (byId) {
                        bodyId = String(byId.id);
                    } else {
                        const byName = bodyTypes.find(b =>
                            getLocalizedField(b, "categoryName", lang) === item.bodyType ||
                            getLocalizedField(b, "name", lang) === item.bodyType ||
                            b.categoryName === item.bodyType ||
                            b.name === item.bodyType
                        );
                        if (byName) bodyId = String(byName.id);
                    }
                }

                // ==== 回填 form ====
                setType(Number(item.categoryId) || 1);
                setCondition(item.condition || 'used');
                setAreaCodePhone(areaPhone);
                setAreaCodeWhatsapp(areaWa);
                setForm(prev => ({
                    ...prev,
                    title: item.title || '',
                    brand: brandId,
                    model: item.modelName || item.modelNameEn || item.modelNameFr || '',
                    year: String(item.carYear),
                    mileage: item.mileage != null ? String(item.mileage) : '',
                    fuelType: ft,
                    transmission: tr,
                    bodyType: bodyId,
                    city: cityId,
                    color: item.color || '',
                    price: item.priceGnf ? String(item.priceGnf) : '',
                    description: item.description || '',
                    contactName: item.contactName || '',
                    contactPhone: phone,
                    whatsapp: wa,
                    email: item.email || '',
                    negotiable: item.priceNegotiable === 1 || item.priceNegotiable === true,
                }));

                // ==== 保留原图片 ====
                const imgs = Array.isArray(item.images) ? item.images : [];
                setExistingImages(imgs);
                setImages([]); // 用户没选新图则用 existingImages

            } catch (e) {
                console.error('加载编辑详情失败', e);
                setToast({ message: '加载详情失败：' + (e.message || ''), type: 'error' });
                setTimeout(() => setToast(null), 4000);
            } finally {
                if (!cancelled) setEditLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [brands.length, cities.length, fuelTypes.length, transmissions.length, bodyTypes.length, editId, lang]);


    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        if (errors[field] || (field === "negotiable" && errors.price)) {
            setErrors(prev => {
                const next = { ...prev };
                delete next[field];
                if (field === "negotiable") delete next.price;
                return next;
            });
        }
    };
    const validate = () => {
        const newErrors = {};
        if (!type) newErrors.type = t("publish.validation.postType");
        if (!form.title?.trim()) newErrors.title = t("publish.validation.brand");
        if (!form.brand) newErrors.brand = t("publish.validation.brand");
        if (!form.model?.trim()) newErrors.model = t("publish.validation.model");
        if (!form.year) newErrors.year = t("publish.validation.year");
        if (!form.mileage) newErrors.mileage = t("publish.validation.mileage");
        if (!form.fuelType) newErrors.fuelType = t("publish.validation.fuelType");
        if (!form.transmission) newErrors.transmission = t("publish.validation.transmission");
        if (!form.city) newErrors.city = t("publish.validation.city");
        if (!form.price && !form.negotiable) newErrors.price = t("publish.validation.priceOrNegotiable");
        if (!form.description?.trim()) newErrors.description = t("publish.validation.description");
        if (!images?.length && !existingImages?.length) newErrors.images = t("publish.validation.images");
        if (!form.contactName?.trim()) newErrors.contactName = t("publish.validation.contactName");
        if (!form.contactPhone?.trim()) newErrors.contactPhone = t("publish.validation.contactPhone");
        if (!form.whatsapp?.trim()) newErrors.whatsapp = t("publish.validation.whatsapp");
        setErrors(newErrors);
        return newErrors;   // 返回对象（而非 boolean），避免 state 异步更新问题
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isEdit && !agree) {
            setAgreeError(t("register.validation.agreeTerms"));
            return;
        }
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            // 页面上每个字段已有红色提示，toast 给第一个错误做汇总
            const firstMsg = Object.values(errs)[0];
            setToast({ message: firstMsg || t("publish.validation.checkFields"), type: 'error' });
            setTimeout(() => setToast(null), 3000);
            return;
        }
        setSubmitting(true);
    setUploadProgress(0);
    setPublishStep(null);
    try {
        // 图片处理：保留 existingImages（已删的已从数组移除）+ 新上传图片
        let imageUrls = [...existingImages];
        let cover = existingImages[0] || '';
        if (images.length > 0) {
            setPublishStep('upload');
            const uploadRes = await listingApi.uploadImages(
                'listing', isEdit ? Number(editId) : 0, images,
                (p) => setUploadProgress(p)   // 实时上传进度
            );
            const newUrls = uploadRes.data || [];
            imageUrls = [...existingImages, ...newUrls];
            setUploadProgress(95);
        }
        cover = imageUrls[0] || '';

        // 品牌名称：从 brands 数组中按当前语言取值
        const brandName = brands.find(b => b.id === Number(form.brand)) ? getLocalizedField(brands.find(b => b.id === Number(form.brand)), "name", lang) : '';
        // 城市名称：从 cities 数组中按当前语言取值
        const cityName = cities.find(c => c.id === Number(form.city)) ? getLocalizedField(cities.find(c => c.id === Number(form.city)), "name", lang) : '';
        // 分类 ID
        const categoryId = Number(type);

        const payload = {
            title: form.title.trim(),
            description: form.description?.trim() || '',
            brand: brandName,
            model: form.model.trim(),
            city: cityName,
            priceGnf: Number(form.price),
            priceNegotiable: form.negotiable ? 1 : 0,
            carYear: Number(form.year),
            mileage: Number(form.mileage),
            fuelType: form.fuelType,
            transmission: form.transmission,
            bodyType: form.bodyType ? Number(form.bodyType) : null,
            condition,
            color: form.color?.trim() || '',
            categoryId,
            images: imageUrls,
            cover,
            contactName: form.contactName.trim(),
            contactPhone: (areaCodePhone + ' ' + form.contactPhone.trim()).trim(),
            whatsapp: (areaCodeWhatsapp + ' ' + form.whatsapp.trim()).trim(),
            email: form.email?.trim() || '',
        };
        setPublishStep('create');
        const res = isEdit
            ? await listingApi.update(editId, payload)
            : await listingApi.create(payload);

        // 兼容多种后端返回格式：
        //   axios:   { code: 200, data: 41 }
        //   axios:   { code: 200, data: { id: 41 } }
        //   原始值: 41
        //   editId: "123"
        let rawId = res?.data; // 优先取 axios 的 data 字段
        if (rawId == null || rawId === '') rawId = res?.id; // 没有 data 就看 res.id
        if (rawId == null || rawId === '') rawId = editId; // edit 模式兜底用 editId
        // 如果是对象（{ id: 41 }），取其 id 字段
        if (typeof rawId === 'object' && rawId !== null) rawId = rawId.id ?? rawId.data;
        const finalId = String(rawId ?? '');

        if (!finalId) {
            throw new Error('Annonce créée mais impossible de récupérer son ID');
        }

        // 创建/更新成功
        setUploadProgress(100);
        setPublishStep('done');
        setToast({ message: isEdit ? t("publish.editSuccess") : t("publish.publishSuccess"), type: 'success' });

        // 成功立即跳转，不等 2 秒（遮罩层会覆盖 toast，用户已经在看 spinner 了）
        if (typeof onSubmitted === "function") {
            onSubmitted(finalId);
        } else {
            router.push(langHref(`/detail/${finalId}`));
        }
    } catch (err) {
        console.error('发布失败', err);
        setToast({ message: err.message || (isEdit ? t("publish.editFailed") : t("publish.publishFailed")), type: 'error' });
        setTimeout(() => setToast(null), 3000);
    } finally {
        setSubmitting(false);
        // 进度条在 finally 不 reset，让用户看到失败时停在哪一步
    }
    };
    
    const handleSaveDraft = () => {
        validate();
    };
    const handleFileClick = () => {
        fileInputRef.current?.click();
    };
    const handleFileChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        const remaining = 10 - images.length;
        if (remaining <= 0) {
            setToast({ message: t("publish.maxImages"), type: "error" });
            setTimeout(() => setToast(null), 3000);
            return;
        }
        const validFiles = files.slice(0, remaining).filter(f => {
            if (!f.type.startsWith('image/')) return false;
            if (f.size > 10 * 1024 * 1024) return false;
            return true;
        });
        setImages(prev => [...prev, ...validFiles]);
        if (errors.images) {
            setErrors(prev => { const n = { ...prev }; delete n.images; return n; });
        }
        e.target.value = '';
    };
    const handleRemoveImage = (index) => {
        setImages(prev => prev.filter((_, i) => i !== index));
    };
    const handleDrop = (e) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files || []);
        if (files.length === 0) return;
        const remaining = 10 - images.length;
        if (remaining <= 0) {
            setToast({ message: t("publish.maxImages"), type: "error" });
            setTimeout(() => setToast(null), 3000);
            return;
        }
        const validFiles = files.slice(0, remaining).filter(f => {
            if (!f.type.startsWith('image/')) return false;
            if (f.size > 10 * 1024 * 1024) return false;
            return true;
        });
        setImages(prev => [...prev, ...validFiles]);
        if (errors.images) {
            setErrors(prev => { const n = { ...prev }; delete n.images; return n; });
        }
    };
    return (<div className="container">
      {isEdit && editLoading && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 10,
          background: 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              border: '3px solid #e5e7eb', borderTopColor: 'var(--color-brand, #2563eb)',
              animation: 'spin 0.9s linear infinite',
            }} />
          </div>
        </div>
      )}
      <div className="form-layout" style={isEdit ? { gridTemplateColumns: '1fr' } : undefined}>
        <main>
          <div className="form-stepper">
            {STEPS.map((s) => (<div key={s.num} className={`form-step ${s.num < currentStep ? "done" : ""} ${s.num === currentStep ? "active" : ""}`}>
                <div className="form-step-num">{s.num < currentStep ? "✓" : s.num}</div>
                <div className="form-step-label">{s.label}</div>
              </div>))}
          </div>

          <form onSubmit={handleSubmit}>
            {toast && <div className={`toast-popup${toast.type === 'success' ? ' success' : ''}`}>{toast.message}</div>}
            <div className="form-section">
              <div className="form-section-title">{t("publish.sectionPostTypeTitle")} <span className="required">*</span></div>
              <div className="form-section-desc">{t("publish.sectionPostTypeDesc")}</div>
              <div className="form-radio-group">
                {loading ? (
                  <div style={{ fontSize: 13, color: "#6B7280", padding: "8px 0" }}>{t("publish.loading")}</div>
                ) : categories.length === 0 ? (
                  <div style={{ fontSize: 13, color: "#6B7280", padding: "8px 0" }}>{t("publish.noCategories")}</div>
                ) : categories.map((c) => (<label key={c.id} className={`form-radio ${type === c.id ? "checked" : ""}`}>
                    <input type="radio" name="type" value={c.id} checked={type === c.id} onChange={() => { setType(c.id); if (errors.type) setErrors(prev => { const n = { ...prev }; delete n.type; return n; }); }}/>
                    <div className="form-radio-label">{getLocalizedField(c, "categoryName", lang)}</div>
                  </label>))}
              </div>
              {errors.type && <div className="form-error">{errors.type}</div>}
            </div>

            <div className="form-section">
              <div className="form-section-title">{t("publish.sectionVehicleTitle")}</div>
              <div className="form-section-desc">{t("publish.sectionVehicleDesc")}</div>
              <div className="form-grid">
                <div className="form-group full">
                  <label className="form-label">{t("publish.adTitle")} <span className="required">*</span></label>
                  <input type="text" className="form-input" placeholder={t("publish.adTitlePh")} value={form.title} onChange={(e) => handleChange('title', e.target.value)}/>
                  {errors.title && <div className="form-error">{errors.title}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">{t("publish.brand")} <span className="required">*</span></label>
                  <SearchableSelect
                    value={form.brand}
                    onChange={(v) => handleChange('brand', v)}
                    options={brands.map(b => ({ value: String(b.id), label: getLocalizedField(b, "name", lang) }))}
                    placeholder={t("publish.selectBrand")}
                    emptyText={t("publish.noBrandFound")}
                  />
                  {errors.brand && <div className="form-error">{errors.brand}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">{t("publish.model")} <span className="required">*</span></label>
                  <input type="text" className="form-input" placeholder={t("publish.modelPh")} value={form.model} onChange={(e) => handleChange('model', e.target.value)}/>
                  {errors.model && <div className="form-error">{errors.model}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">{t("publish.year")} <span className="required">*</span></label>
                  <select className="form-select" value={form.year} onChange={(e) => handleChange('year', e.target.value)}>
                    <option value="">{t("publish.selectYear")}</option>
                    <option value="0">{t("publish.yearNA")}</option>
                    {YEARS.map((y) => (<option key={y} value={y}>{y}</option>))}
                  </select>
                  {errors.year && <div className="form-error">{errors.year}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">{t("publish.mileage")} <span className="required">*</span></label>
                  <input type="number" className="form-input" placeholder={t("publish.mileagePh")} value={form.mileage} onChange={(e) => handleChange('mileage', e.target.value)}/>
                  {errors.mileage && <div className="form-error">{errors.mileage}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">{t("publish.fuelType")} <span className="required">*</span></label>
                  <select className="form-select" value={form.fuelType} onChange={(e) => handleChange('fuelType', e.target.value)}>
                    <option value="">{t("publish.selectFuel")}</option>
                    {fuelTypes.map((f) => (<option key={f.value} value={f.value}>{dictLabel(f)}</option>))}
                  </select>
                  {errors.fuelType && <div className="form-error">{errors.fuelType}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">{t("publish.transmission")} <span className="required">*</span></label>
                  <select className="form-select" value={form.transmission} onChange={(e) => handleChange('transmission', e.target.value)}>
                    <option value="">{t("publish.selectTransmission")}</option>
                    {transmissions.map((tr) => (<option key={tr.value} value={tr.value}>{dictLabel(tr)}</option>))}
                  </select>
                  {errors.transmission && <div className="form-error">{errors.transmission}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">{t("publish.bodyType")}</label>
                  <select className="form-select" value={form.bodyType} onChange={(e) => handleChange('bodyType', e.target.value)}>
                    <option value="">{t("publish.selectBodyType")}</option>
                    {bodyTypes.map((b) => (<option key={b.id} value={String(b.id)}>{getLocalizedField(b, "categoryName", lang)}</option>))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{t("publish.condition")}</label>
                  <div className="form-radio-group">
                    <label className={`form-radio ${condition === "used" ? "checked" : ""}`} style={{ flex: 1 }}>
                      <input type="radio" name="condition" value="used" checked={condition === "used"} onChange={() => setCondition("used")}/>
                      <div className="form-radio-label">{t("publish.conditionUsed")}</div>
                    </label>
                    <label className={`form-radio ${condition === "new" ? "checked" : ""}`} style={{ flex: 1 }}>
                      <input type="radio" name="condition" value="new" checked={condition === "new"} onChange={() => setCondition("new")}/>
                      <div className="form-radio-label">{t("publish.conditionNew")}</div>
                    </label>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">{t("publish.city")} <span className="required">*</span></label>
                  <select className="form-select" value={form.city} onChange={(e) => handleChange('city', e.target.value)}>
                    <option value="">{t("publish.selectCity")}</option>
                    {cities.map((c) => (<option key={c.id} value={c.id}>{getLocalizedField(c, "name", lang)}</option>))}
                  </select>
                  {errors.city && <div className="form-error">{errors.city}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">{t("publish.color")}</label>
                  <input type="text" className="form-input" placeholder={t("publish.colorPh")} value={form.color} onChange={(e) => handleChange('color', e.target.value)}/>
                </div>
                <div className="form-group full">
                  <label className="form-label">{t("publish.price")} <span className="required">*</span></label>
                  <div className="flex gap-3">
                    <input type="number" className="form-input" placeholder={t("publish.pricePh")} style={{ flex: 1 }} value={form.price} onChange={(e) => handleChange('price', e.target.value)}/>
                    <label style={{
                        display: "inline-flex", alignItems: "center", gap: 8,
                        padding: "0 16px", height: 42,
                        border: form.negotiable ? "1.5px solid var(--color-brand)" : "1.5px solid var(--color-border)",
                        borderRadius: 10,
                        background: form.negotiable ? "var(--color-brand-lighter)" : "transparent",
                        cursor: "pointer", userSelect: "none", whiteSpace: "nowrap",
                        transition: "all 200ms",
                    }}>
                      <input type="checkbox" checked={form.negotiable} onChange={(e) => handleChange('negotiable', e.target.checked)} style={{ width: 16, height: 16, accentColor: "var(--color-brand)", cursor: "pointer" }}/>
                      <span style={{ fontSize: 14, fontWeight: 500 }}>{t("publish.negotiableYes")}</span>
                    </label>
                  </div>
                  {errors.price && <div className="form-error">{errors.price}</div>}
                </div>
                <div className="form-group full">
                  <label className="form-label">{t("publish.description")} <span className="required">*</span></label>
                  <textarea className="form-textarea" placeholder={t("publish.descriptionPh")} value={form.description} onChange={(e) => handleChange('description', e.target.value)}/>
                  <div className="form-help">{t("publish.descriptionHelp")}</div>
                  {errors.description && <div className="form-error">{errors.description}</div>}
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-title">{t("publish.sectionImagesTitle")} <span className="required">*</span></div>
              <div className="form-section-desc">{t("publish.sectionImagesDesc")}</div>
              {images.length === 0 && existingImages.length === 0 && (
               <div className="image-uploader" onClick={handleFileClick} onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} style={{ cursor: "pointer" }}>
                 <div className="image-uploader-icon">
                   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width={28} height={28}>
                     <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                     <polyline points="17 8 12 3 7 8"/>
                     <line x1="12" y1="3" x2="12" y2="15"/>
                   </svg>
                 </div>
                 <div className="image-uploader-text">{t("publish.imageUploaderText")}</div>
                 <div className="image-uploader-hint">{t("publish.imageUploaderHint")}</div>
               </div>
               )}
               <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleFileChange}/>

               {(images.length > 0 || existingImages.length > 0) && (
               <div className="image-preview-list">
                 {/* ==== 已存在图片（后端 URL）==== */}
                 {existingImages.map((url, i) => (<div key={`ext-${i}`} className="image-preview">
                     <img src={url} alt={`已有图 ${i + 1}`}/>
                     <button type="button" className="image-preview-remove" onClick={() => {
                         setExistingImages(prev => prev.filter((_, idx) => idx !== i));
                     }}>×</button>
                     {i === 0 && <span className="image-preview-cover">{t("publish.previewCover")}</span>}
                   </div>))}
                 {/* ==== 新上传图片（File 对象）==== */}
                 {images.map((file, i) => (<div key={`new-${i}`} className="image-preview">
                     <img src={URL.createObjectURL(file)} alt={`预览图 ${i + 1}`}/>
                     <button type="button" className="image-preview-remove" onClick={() => handleRemoveImage(i)}>×</button>
                     {existingImages.length + i === 0 && <span className="image-preview-cover">{t("publish.previewCover")}</span>}
                   </div>))}
                 {(existingImages.length + images.length) < 10 && <div className="image-preview" onClick={handleFileClick} style={{
             background: "var(--color-bg-alt)",
             display: "flex",
             alignItems: "center",
             justifyContent: "center",
             color: "var(--color-text-muted)",
             cursor: "pointer",
         }}>
                   <div style={{ textAlign: "center" }}>
                     <div style={{ fontSize: 24 }}>+</div>
                     <div style={{ fontSize: 11 }}>{t("publish.add")}</div>
                   </div>
                 </div>}
               </div>
               )}
              {errors.images && <div className="form-error" style={{ marginTop: 8 }}>{errors.images}</div>}
            </div>

            <div className="form-section">
              <div className="form-section-title">{t("publish.sectionContactTitle")}</div>
              <div className="form-section-desc">{t("publish.sectionContactDesc")}</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">{t("publish.contactName")} <span className="required">*</span></label>
                  <input type="text" className="form-input" placeholder={t("publish.contactNamePh")} value={form.contactName} onChange={(e) => handleChange('contactName', e.target.value)}/>
                  {errors.contactName && <div className="form-error">{errors.contactName}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">{t("publish.contactPhone")} <span className="required">*</span></label>
                  <div className="flex gap-2">
                    <select className="form-select" value={areaCodePhone} onChange={(e) => setAreaCodePhone(e.target.value)} style={{ width: 160, padding: "8px 10px" }}>
                      {AREA_CODES.map((code) => (
                        <option key={code} value={code}>{code} · {t(`publish.countryCodes.${code}`)}</option>
                      ))}
                    </select>
                    <input type="tel" className="form-input" placeholder={t("publish.contactPhonePh")} style={{ flex: 1 }} value={form.contactPhone} onChange={(e) => handleChange('contactPhone', e.target.value)}/>
                  </div>
                  {errors.contactPhone && <div className="form-error">{errors.contactPhone}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">WhatsApp <span className="required">*</span></label>
                  <div className="flex gap-2">
                    <select className="form-select" value={areaCodeWhatsapp} onChange={(e) => setAreaCodeWhatsapp(e.target.value)} style={{ width: 160, padding: "8px 10px" }}>
                      {AREA_CODES.map((code) => (
                        <option key={code} value={code}>{code} · {t(`publish.countryCodes.${code}`)}</option>
                      ))}
                    </select>
                    <input type="tel" className="form-input" placeholder={t("publish.whatsappPh")} style={{ flex: 1 }} value={form.whatsapp} onChange={(e) => handleChange('whatsapp', e.target.value)}/>
                  </div>
                  {errors.whatsapp && <div className="form-error">{errors.whatsapp}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">{t("publish.email")}</label>
                  <input type="email" className="form-input" placeholder={t("publish.emailPh")} value={form.email} onChange={(e) => handleChange('email', e.target.value)}/>
                </div>
                {!isEdit && (
                <div className="form-group full">
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--color-text-secondary)", cursor: "pointer", marginTop: 4 }}>
                    <input type="checkbox" checked={agree} onChange={(e) => { setAgree(e.target.checked); setAgreeError(""); }} />
                    {t("register.agreeTerms")}{" "}
                    <button type="button" onClick={(e) => { e.preventDefault(); setTermsOpen(true); }} style={{ color: "var(--color-brand)", background: "none", border: "none", cursor: "pointer", padding: 0, fontWeight: 500 }}>{t("register.termsLink")}</button>
                    {" + "}
                    <button type="button" onClick={(e) => { e.preventDefault(); setPrivacyOpen(true); }} style={{ color: "var(--color-brand)", background: "none", border: "none", cursor: "pointer", padding: 0, fontWeight: 500 }}>{t("register.privacyLink")}</button>
                  </label>
                  {agreeError && <div style={{ color: "#d32f2f", fontSize: 12, marginTop: 4 }}>{agreeError}</div>}
                </div>
                )}
              </div>
            </div>

            {/* 发布中遮罩 —— submitting 时覆盖在表单上，防止用户误操作 */}
            {submitting && (
              <div style={{
                position: "fixed", inset: 0, zIndex: 9999,
                background: "rgba(255,255,255,0.85)", backdropFilter: "blur(3px)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <div style={{
                  background: "#fff", borderRadius: 14, padding: "28px 36px",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.12)", textAlign: "center",
                  border: "1px solid var(--color-border, #e5e7eb)",
                  minWidth: 220,
                }}>
                  <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       strokeWidth="2.5" strokeLinecap="round"
                       style={{ color: "var(--color-brand, #228b22)", animation: "spin 1s linear infinite", display: "block", margin: "0 auto 14px" }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                  </svg>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text, #111)" }}>
                    {publishStep === 'upload' && t("publish.stepUploading")}
                    {publishStep === 'create' && t("publish.stepCreating")}
                    {publishStep === 'done' && t("publish.stepDone")}
                    {!publishStep && t("publish.submitting")}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: "var(--color-text-muted, #888)" }}>
                    {!images.length ? t("publish.step2Create") : (
                      publishStep === 'upload' ? t("publish.step1Upload") : t("publish.step2Create")
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button type="submit" className="btn btn-primary btn-lg" disabled={submitting}>
                {submitting ? (<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                       style={{ animation: "spin 1s linear infinite" }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                  </svg>
                  {t("publish.submitting")}
                </span>) : (<><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width={18} height={18}>
                  <path d="M5 13l4 4L19 7"/>
                </svg>
                {isEdit ? t("publish.editNow") : t("publish.publishNow")}</>)}
              </button>
              {!isEdit && <Link href="/promote" className="btn btn-accent btn-lg" style={{ marginLeft: "auto" }}>
                {t("publish.promoCta")}
              </Link>}
            </div>
          </form>
        </main>

        {!isEdit && (
        <aside>
          {/* ─── 发布规则（精简） ─── */}
          <div className="summary-card" style={{ marginBottom: 16 }}>
            <div className="summary-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width={16} height={16} style={{ color: "var(--color-brand)" }}>
                <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="10" />
              </svg>
              {t("publish.rules.title")}
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0" }}>
              {[
                t("publish.rules.r1"),
                t("publish.rules.r2"),
                t("publish.rules.r5"),
              ].map((rule, i) => (
                <li key={i} style={{ display: "flex", gap: 8, fontSize: 12, lineHeight: 1.6, marginBottom: 6, color: "var(--color-text-primary)" }}>
                  <span style={{ flexShrink: 0, width: 4, height: 4, borderRadius: "50%", background: "var(--color-brand)", marginTop: 8 }} />
                  <span>{rule}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* ─── 推广建议（精简） ─── */}
          <div className="summary-card">
            <div className="summary-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width={16} height={16} style={{ color: "var(--color-brand)" }}>
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
              {t("publish.promoSide.title")}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
              {[
                { title: t("publish.promoSide.plan1Title"), desc: t("publish.promoSide.plan1Desc"), price: t("publish.promoSide.plan1Price"), days: t("publish.promoSide.plan1Days") },
                { title: t("publish.promoSide.plan3Title"), desc: t("publish.promoSide.plan3Desc"), price: t("publish.promoSide.plan3Price"), days: t("publish.promoSide.plan3Days"), popular: true },
              ].map((plan, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "8px 10px",
                  border: plan.popular ? "1.5px solid var(--color-brand)" : "1px solid var(--color-border)",
                  borderRadius: 8,
                  background: plan.popular ? "var(--color-brand-lighter)" : "var(--color-surface)",
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                      {plan.title}
                      {plan.popular && <span style={{ fontSize: 8, padding: "1px 5px", background: "var(--color-brand)", color: "white", borderRadius: 8, fontWeight: 700, letterSpacing: 0.3 }}>HOT</span>}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{plan.desc}</div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: plan.popular ? "var(--color-brand)" : "var(--color-text-primary)" }}>{plan.price}</div>
                    <div style={{ fontSize: 9.5, color: "var(--color-text-secondary)" }}>{plan.days}</div>
                  </div>
                </div>
              ))}
            </div>
            <Link href="/promote" className="btn btn-accent btn-block" style={{ marginTop: 10, padding: "7px 12px", fontSize: 12 }}>
              {t("publish.promoSide.cta")}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width={14} height={14} style={{ marginLeft: 5 }}>
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </aside>
        )}
      </div>

    {termsOpen && (
      <div onClick={() => setTermsOpen(false)} style={modalOverlay}>
        <div onClick={(e) => e.stopPropagation()} style={modalContent}>
          <div style={modalHeader}>
            <h2 style={modalTitle}>{t("register.termsLink")}</h2>
            <button onClick={() => setTermsOpen(false)} style={closeBtn}>✕</button>
          </div>
          <div style={modalBody}>
            <p style={{ fontWeight: 600, color: "var(--color-text)", lineHeight: 1.7, margin: "0 0 20px", padding: "12px 16px", background: "var(--color-bg-hover)", borderRadius: 10 }}>
              {t("terms.highlight")}
            </p>
            {[1,2,3,4,5,6,7,8,9,10].map(n => {
              const title = t(`terms.s${n}Title`);
              const p1 = t(`terms.s${n}p1`);
              const p2 = t(`terms.s${n}p2`);
              const intro = t(`terms.s${n}Intro`);
              const listKey = `terms.s${n}list`;
              const list = t(listKey);
              const listArr = Array.isArray(list) ? list : null;
              return (
                <div key={n} style={{ marginBottom: 20 }}>
                  <h3 style={sectionTitle}>{title}</h3>
                  {p1 && <p style={sectionP}>{p1}</p>}
                  {p2 && <p style={sectionP}>{p2}</p>}
                  {intro && <p style={{ ...sectionP, fontWeight: 500 }}>{intro}</p>}
                  {listArr && listArr.length > 0 && (
                    <ul style={{ paddingLeft: 20, margin: "6px 0 0", color: "var(--color-text-secondary)", lineHeight: 1.9, fontSize: 14 }}>
                      {listArr.map((item, i) => <li key={i}>{item}</li>)}
                    </ul>
                  )}
                </div>
              );
            })}
            <div style={{ marginTop: 24, padding: 16, background: "var(--color-bg-hover)", borderRadius: 10 }}>
              <h3 style={{ ...sectionTitle, marginBottom: 8 }}>{t("terms.contactBoxTitle")}</h3>
              <p style={sectionP}>{t("terms.contactBoxMsg")}</p>
              <p style={{ ...sectionP, margin: "4px 0" }}>{t("terms.contactEmail")} 18918855336@126.com</p>
              <p style={sectionP}>{t("terms.contactOperator")} +86-21-37788331</p>
            </div>
          </div>
        </div>
      </div>
    )}

    {privacyOpen && (
      <div onClick={() => setPrivacyOpen(false)} style={modalOverlay}>
        <div onClick={(e) => e.stopPropagation()} style={modalContent}>
          <div style={modalHeader}>
            <h2 style={modalTitle}>{t("register.privacyLink")}</h2>
            <button onClick={() => setPrivacyOpen(false)} style={closeBtn}>✕</button>
          </div>
          <div style={modalBody}>
            {[1,2,3,4,5,6,7,8].map(n => {
              const title = t(`privacy.s${n}Title`);
              const p1 = t(`privacy.s${n}p1`);
              const p2 = t(`privacy.s${n}p2`);
              const intro = t(`privacy.s${n}Intro`);
              const list = t(`privacy.s${n}list`);
              const listArr = Array.isArray(list) ? list : null;
              return (
                <div key={n} style={{ marginBottom: 20 }}>
                  <h3 style={sectionTitle}>{title}</h3>
                  {p1 && <p style={sectionP}>{p1}</p>}
                  {p2 && <p style={sectionP}>{p2}</p>}
                  {intro && <p style={{ ...sectionP, fontWeight: 500 }}>{intro}</p>}
                  {listArr && listArr.length > 0 && (
                    <ul style={{ paddingLeft: 20, margin: "6px 0 0", color: "var(--color-text-secondary)", lineHeight: 1.9, fontSize: 14 }}>
                      {listArr.map((item, i) => <li key={i}>{item}</li>)}
                    </ul>
                  )}
                </div>
              );
            })}
            <div style={{ marginTop: 24, padding: 16, background: "var(--color-bg-hover)", borderRadius: 10 }}>
              <h3 style={{ ...sectionTitle, marginBottom: 8 }}>{t("privacy.contactBoxTitle")}</h3>
              <p style={sectionP}>{t("privacy.contactBoxMsg")}</p>
              <p style={{ ...sectionP, margin: "4px 0" }}>{t("privacy.contactEmail")} 18918855336@126.com</p>
              <p style={sectionP}>{t("privacy.contactOperator")} +86-21-37788331</p>
            </div>
          </div>
        </div>
      </div>
    )}
    </div>);
}
